import React from 'react'
import { Button, Popover } from '@material-ui/core'
import { PopoverState } from '../../../../hooks/usePopover'
import { Container } from '../../../Container'
import { DateTime } from 'luxon'
import { Theme } from '@material-ui/core'
import { ezScheduleDetails } from '../../../../models/WorkOrder/types'
import { UpdateEzScheduleParams } from '../../../../hooks/useEzSchedule/types'
import { useTimezone } from '../../../../contexts/timezone'
import { typographyStyle } from '../../../../theme/typographyStyle'

interface Props {
    theme: Theme
    ezSchedule: ezScheduleDetails
    updateEzSchedule: (params: UpdateEzScheduleParams) => void
    popoverState: PopoverState<any>
    selectedDate: DateTime
    handleChooseStartTransferDate: (clickX: number) => void
    handleChooseEndTransferDate: (clickX: number) => void
    handleChooseDestUnitReadyDate: (clickX: number) => void
    handleChooseDtUnitReadyDate: (clickX: number) => void
    selectedPhaseId: number
}

export const HeaderPopover = (props: Props) => {
    const {
        theme,
        popoverState,
        selectedDate,
        ezSchedule,
        updateEzSchedule,
        selectedPhaseId,
    } = props
    const { currentTimezone } = useTimezone()

    const buttonStyle: React.CSSProperties = {
        textTransform: 'none',
        minHeight: 32,
        fontSize: '0.875rem',
    }

    const sectionStyle: React.CSSProperties = {
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(0.5),
        marginBottom: theme.spacing(1),
        border: `1px solid ${theme.palette.grey[200]}`,
    }

    const headerStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.subtitle2),
        fontWeight: 700,
        marginBottom: theme.spacing(1),
        color: theme.palette.text.primary,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    }

    const labelStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.body2),
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    }

    const valueStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.body2),
        fontWeight: 500,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(1),
    }

    const selectedPhase = ezSchedule?.phases.find(
        (phase) => phase.phase.id === selectedPhaseId,
    )

    const formatPhaseDate = (iso: string | null | undefined) => {
        if (!iso) return 'Not Set'
        return DateTime.fromISO(iso)
            .setZone(currentTimezone)
            .toFormat('MM/dd/yyyy')
    }

    const getTransferStartDate = () =>
        formatPhaseDate(selectedPhase?.transfer_start_date)
    const getTransferEndDate = () =>
        formatPhaseDate(selectedPhase?.transfer_end_date)
    const getDestUnitReadyDate = () =>
        formatPhaseDate(selectedPhase?.dest_unit_ready_date)
    const getDtUnitReadyDate = () =>
        formatPhaseDate(selectedPhase?.dt_unit_ready_date)

    const selectedDateMs = selectedDate
        .setZone(currentTimezone)
        .startOf('day')
        .toMillis()
    const selectedDateIsDayOff = selectedPhase?.day_off_list.some((dayOff) => {
        const dayOffMs = DateTime.fromISO(dayOff.date)
            .setZone(currentTimezone)
            .startOf('day')
            .toMillis()
        return dayOffMs === selectedDateMs
    })

    return (
        <Popover
            open={popoverState.isOpen}
            anchorEl={popoverState.anchorEl}
            onClose={popoverState.handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            PaperProps={{
                style: {
                    minWidth: 280,
                    padding: theme.spacing(2),
                },
            }}
        >
            <Container
                style={{
                    flexDirection: 'column',
                    gap: theme.spacing(1),
                }}
            >
                {/* Date Header */}
                <div
                    style={{
                        ...typographyStyle(theme.typography.h6),
                        fontWeight: 700,
                        textAlign: 'center',
                        padding: theme.spacing(1),
                        color: theme.palette.text.primary,
                    }}
                >
                    {selectedDate.toFormat('MM/dd/yyyy')}
                </div>

                {/* Transfer Dates Section */}
                <div style={sectionStyle}>
                    <div style={headerStyle}>📋 Transfer Dates</div>

                    {/* Transfer Date Status */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: theme.spacing(1),
                        }}
                    >
                        <div>
                            <div style={labelStyle}>Start:</div>
                            <div style={valueStyle}>
                                {getTransferStartDate()}
                            </div>
                        </div>
                        <div>
                            <div style={labelStyle}>End:</div>
                            <div style={valueStyle}>{getTransferEndDate()}</div>
                        </div>
                    </div>

                    <Container
                        style={{
                            flexDirection: 'row',
                            gap: theme.spacing(0.5),
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!!selectedPhase?.transfer_start_date}
                            onClick={() => {
                                const anchorEl = popoverState.anchorEl
                                if (anchorEl) {
                                    props.handleChooseStartTransferDate(
                                        anchorEl.getBoundingClientRect().x,
                                    )
                                }
                                popoverState.handleClose()
                            }}
                            style={{ ...buttonStyle, flex: 1 }}
                        >
                            Set Start
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={!selectedPhase?.transfer_start_date}
                            onClick={() => {
                                const anchorEl = popoverState.anchorEl
                                if (anchorEl) {
                                    props.handleChooseEndTransferDate(
                                        anchorEl.getBoundingClientRect().x,
                                    )
                                }
                                popoverState.handleClose()
                            }}
                            style={{ ...buttonStyle, flex: 1 }}
                        >
                            Set End
                        </Button>

                        <Button
                            variant="contained"
                            color="secondary"
                            disabled={
                                !selectedPhase?.transfer_start_date &&
                                !selectedPhase?.transfer_end_date
                            }
                            onClick={() => {
                                updateEzSchedule({
                                    transfer_start_date: null,
                                    transfer_end_date: null,
                                })
                                popoverState.handleClose()
                            }}
                            style={{ ...buttonStyle, flex: 1 }}
                        >
                            Clear
                        </Button>
                    </Container>
                </div>

                <div style={sectionStyle}>
                    <div style={{ ...headerStyle, color: '#ef6c00' }}>
                        Destination unit ready
                    </div>
                    <div style={valueStyle}>{getDestUnitReadyDate()}</div>
                    <Container
                        style={{
                            flexDirection: 'row',
                            gap: theme.spacing(0.5),
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                const anchorEl = popoverState.anchorEl
                                if (anchorEl) {
                                    props.handleChooseDestUnitReadyDate(
                                        anchorEl.getBoundingClientRect().x,
                                    )
                                }
                                popoverState.handleClose()
                            }}
                            style={{ ...buttonStyle, flex: 1 }}
                        >
                            Set
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            disabled={!selectedPhase?.dest_unit_ready_date}
                            onClick={() => {
                                updateEzSchedule({
                                    dest_unit_ready_date: null,
                                })
                                popoverState.handleClose()
                            }}
                            style={{ ...buttonStyle, flex: 1 }}
                        >
                            Clear
                        </Button>
                    </Container>
                </div>

                <div style={sectionStyle}>
                    <div style={{ ...headerStyle, color: '#e65100' }}>
                        Double transfer unit ready
                    </div>
                    <div style={valueStyle}>{getDtUnitReadyDate()}</div>
                    <Container
                        style={{
                            flexDirection: 'row',
                            gap: theme.spacing(0.5),
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                const anchorEl = popoverState.anchorEl
                                if (anchorEl) {
                                    props.handleChooseDtUnitReadyDate(
                                        anchorEl.getBoundingClientRect().x,
                                    )
                                }
                                popoverState.handleClose()
                            }}
                            style={{ ...buttonStyle, flex: 1 }}
                        >
                            Set
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            disabled={!selectedPhase?.dt_unit_ready_date}
                            onClick={() => {
                                updateEzSchedule({
                                    dt_unit_ready_date: null,
                                })
                                popoverState.handleClose()
                            }}
                            style={{ ...buttonStyle, flex: 1 }}
                        >
                            Clear
                        </Button>
                    </Container>
                </div>

                {/* Day Off Management Section */}
                <div style={sectionStyle}>
                    <div style={headerStyle}>⏰ Day Off Management</div>
                    <Container
                        style={{
                            flexDirection: 'row',
                            gap: theme.spacing(0.5),
                        }}
                    >
                        {/* TODO: Implement this */}
                        <Button
                            variant="contained"
                            color="default"
                            disabled={selectedDateIsDayOff}
                            onClick={() => {
                                const selectedDateIso = selectedDate
                                    .setZone(currentTimezone)
                                    .startOf('day')
                                    .toUTC()
                                    .toISO()
                                if (!selectedDateIso) return
                                updateEzSchedule({
                                    add_day_off_date: selectedDateIso,
                                })
                                popoverState.handleClose()
                            }}
                            style={{ ...buttonStyle, flex: 1 }}
                        >
                            Set Day Off
                        </Button>

                        <Button
                            variant="outlined"
                            color="default"
                            disabled={!selectedDateIsDayOff}
                            onClick={() => {
                                const selectedDateIso = selectedDate
                                    .setZone(currentTimezone)
                                    .startOf('day')
                                    .toUTC()
                                    .toISO()
                                if (!selectedDateIso) return
                                updateEzSchedule({
                                    remove_day_off_date: selectedDateIso,
                                })
                                popoverState.handleClose()
                            }}
                            style={{ ...buttonStyle, flex: 1 }}
                        >
                            Clear Day Off
                        </Button>
                    </Container>
                </div>

                {/* Close Button */}
                <Button
                    variant="outlined"
                    color="default"
                    onClick={popoverState.handleClose}
                    style={{
                        textTransform: 'none',
                        width: '100%',
                        marginTop: theme.spacing(0.5),
                    }}
                >
                    Close
                </Button>
            </Container>
        </Popover>
    )
}
